import { DeleteFilled, PlusCircleFilled } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
} from 'antd';
import { Link } from 'react-router-dom';

import {
  VoucherFooterButton,
  VoucherTable,
} from '@/pages/vouchers/VoucherComponent';
import { useVoucherFunctions } from '@/pages/vouchers/VoucherFunctions';
import { useEffect, useState } from 'react';
import CustomSelect from '@/utils/search/Search';
import GlobalFormModal from '@/utils/modal/GlobalFormModal';
import LedgerForm from '@/components/forms/master/account/ledger/LedgerForm';
import RadioButtonGroup from '@/common/ant-components/RadioGroupReport';
import {
  getAdFromBS,
  getBsFromAD,
  getTodayDate,
} from '@/utils/helpers/getTodayDate';
import { dateDisabled } from '../reuse/helpers';
import { NewNepaliDatePicker } from '@/common/customNepaliDatePicker';
import { dateType as backendDateType } from '@/common/custom-date-selector-list/helpers';
import useAppData from '@/hooks/useAppData';
import { formatNumberWithComma } from '@/utils/formula/CodeWindow';

const SalesReceiptModal = ({
  onTotalUpdate,
  visible,
  onClose,
  selectedData,
  billNumber,
  dataSource,
  setDataSource,
  cashBankLedgerData,
  refreshTable,
  id,
  receiptVoucherHeader,
  setReceiptVoucherHeader,
}) => {
  const fieldName = 'receiveTo';
  const {
    VoucherData,
    // handleAdd,
    // handleSave,
    // handleDelete,
    // total,
    selectedItems,
    setSelectedItems,
  } = useVoucherFunctions({
    dataSource,
    setDataSource,
    fieldName,
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const app = useAppData();
  const [count, setCount] = useState(0);

  const [dateType, setDateType] = useState(app?.app?.dateType);
  const [total, setTotal] = useState(0);

  // This can be dynamically set for other voucher types like "payTo" for payment vouchers
  const rowTemplate = { [fieldName]: '', amount: 0, remarks: '' };

  const handleSelectChange = (value, record) => {
    const updatedDataSource = dataSource?.map((item) => {
      return item.key === record.key ? { ...item, receiveTo: value } : item;
    });
    setDataSource(updatedDataSource);
    setSelectedItems((prev) => [...prev, value]);
    handleSave(updatedDataSource.find((item) => item.key === record.key)); // Update with new value
  };

  const handleAdd = (rowTemplate) => {
    const newData = { key: dataSource.length, ...rowTemplate };
    setDataSource((prevData) => {
      const newDataSource = [...prevData, newData];
      setSelectedItems(
        newDataSource?.map((item) => item[fieldName]).filter(Boolean),
      );
      return newDataSource;
    });
    setCount((prev) => prev + 1);
  };

  const handleDelete = (key) => {
    if (dataSource.length === 1) {
      // If there's only one row, clear its data (don't delete it)
      const resetData = [
        { key: dataSource[0].key, fieldName: '', amount: 0, remarks: '' },
      ];
      setDataSource(resetData);
      setSelectedItems([]); // Reset selected items since the row is now empty
      recalculateTotal(resetData);
    } else {
      // If there's more than one row, delete the selected row
      const newData = dataSource
        .filter((item) => item.key !== key)
        .map((item, index) => ({
          ...item,
          key: index,
          sno: index + 1,
        }));
      setDataSource(newData);
      setSelectedItems(newData.map((item) => item.receiveTo).filter(Boolean)); // Update selectedItems
      recalculateTotal(newData);
    }
  };

  const handleRadio = (e) => {
    setDateType(e?.target?.value);
  };
  // Recalculate the total amount
  const recalculateTotal = (data) => {
    const total = data?.reduce(
      (acc, item) => acc + parseFloat(item.amount || 0),
      0,
    );
    setTotal(Number(total));
    if (onTotalUpdate) {
      onTotalUpdate(total);
    }
  };

  const handleSave = (row) => {
    const newData = dataSource?.map((item) =>
      item.key === row.key ? { ...item, ...row } : item,
    );
    setDataSource(newData);
    setSelectedItems(newData.map((item) => item[fieldName]).filter(Boolean)); // Update selectedItems
    recalculateTotal(newData);
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      width: '10%',
      align: 'center',
    },
    {
      title: 'Particular',
      dataIndex: fieldName,
      align: 'center',
      width: '40%',
      render: (_, record) => (
        <CustomSelect
          placeholder="Select Ledger"
          style={{ width: '100%' }}
          value={record[fieldName]}
          onChange={(value) => handleSelectChange(value, record)}
          options={
            cashBankLedgerData
              ? cashBankLedgerData?.particularLedger?.map((ledger) => ({
                  key: ledger.referenceId,
                  value: ledger.referenceId,
                  label: ledger.name,
                  disabled:
                    selectedItems.includes(ledger.referenceId) &&
                    record[fieldName] !== ledger.referenceId,
                }))
              : []
          }
          onAddClick={handleAddClick}
        />
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      editable: true,
      align: 'center',
      render: (_, record) => (
        <Input
          value={record.amount}
          className="tw-text-right"
          onChange={(e) => {
            const value = e.target.value;
            const sanitizedValue = value.replace(/^(-?)0+(\d)/, '$1$2');
            handleSave({
              ...record,
              amount: isNaN(e.target.value) ? 0 : sanitizedValue,
            });
          }}
        />
      ),
    },
    {
      title: 'Remark',
      dataIndex: 'remarks',
      editable: true,
      align: 'center',
      render: (_, record) => (
        <Input
          value={record.remarks}
          onChange={(e) => handleSave({ ...record, remarks: e.target.value })}
        />
      ),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <section className="flex gap-1 justify-center">
          <Link>
            <PlusCircleFilled onClick={() => handleAdd(rowTemplate)} />
          </Link>
          <Link className="delete" style={{ color: 'red' }}>
            <DeleteFilled onClick={() => handleDelete(record.key)} />
          </Link>
        </section>
      ),
    },
  ];

  const handleAddClick = () => {
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const tableSummary = (
    <>
      <Table.Summary.Row style={{ lineHeight: 'inherit' }}>
        <Table.Summary.Cell>Total</Table.Summary.Cell>
        <Table.Summary.Cell />
        <Table.Summary.Cell className="tw-text-right">
          {formatNumberWithComma(total)}
        </Table.Summary.Cell>
        <Table.Summary.Cell />
      </Table.Summary.Row>
    </>
  );
  useEffect(() => {
    // onTotalUpdate(total);
    recalculateTotal(dataSource);
  }, [total]);

  useEffect(() => {
    if (selectedData?.entryDate) {
      const currentDate =
        typeof selectedData?.entryDate === 'string'
          ? dayjs(selectedData?.entryDate)
          : selectedData?.entryDate;

      form.setFieldsValue({
        entryDate: currentDate,
        entry_date_bs: getBsFromAD(currentDate),
      });
    }
  }, [selectedData]);

  useEffect(() => {
    setSelectedItems(dataSource.map((item) => item.receiveTo).filter(Boolean));
  }, []);

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        // footer={[
        //   <Button key="back" onClick={onClose}>
        //     Cancel
        //   </Button>,
        //   <Button
        //     key="submit"
        //     type="primary"
        //     onClick={() => {
        //       onClose();
        //     }}
        //   >
        //     Submit
        //   </Button>,
        // ]}
        title={
          <div className="tw-text-center tw-font-[600] tw-text-base">
            Receipt Voucher
          </div>
        }
        footer={
          <VoucherFooterButton id={id} isFromModal={true} onClose={onClose} />
        }
        width={1000}
      >
        <Form form={form}>
          <Row
            style={{
              paddingLeft: '15px',
              paddingRight: '15px',
              // paddingTop: '10px',
            }}
            gutter={[8, 8]}
            justify="space-between"
          >
            <Col xs={24} md={12} lg={4}>
              <div>
                <Form.Item label="Voucher no: " style={{ fontWeight: 500 }}>
                  <Input
                    style={{ width: 'fit-content', height: 'fit-content' }}
                    disabled
                    value={receiptVoucherHeader?.voucherNo}
                  />
                </Form.Item>
              </div>
            </Col>
            {/* <Col xs={12} md={12} lg={15}>
              <p
                className="username"
                style={{
                  alignItems: 'center',
                  // textAlign: 'start',
                  margin: 'auto',
                  fontSize: '20px',
                }}
              >
              </p>
            </Col> */}
          </Row>
          <Row
            style={{
              paddingLeft: '15px',
              paddingRight: '15px',
              paddingTop: '10px',
            }}
            gutter={[8, 8]}
            justify="space-between"
          >
            {/* <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <p className="username">
            Voucher no: <Input disabled value={billNumber?.voucherNumber} />
          </p>
          <p className="username">
            Date: <DatePicker value={selectedData?.entryDate} />
          </p>
        </div> */}

            <Col xs={24} md={10} lg={14}>
              <Form.Item label="Received From:" style={{ fontWeight: 500 }}>
                <Select
                  value={
                    cashBankLedgerData?.receiveFromLedger?.some(
                      (item) => item.referenceId === selectedData?.ledger,
                    )
                      ? selectedData?.ledger
                      : null
                  }
                  style={{ width: 'fit-content' }}
                  disabled
                >
                  {cashBankLedgerData?.receiveFromLedger?.map((ledger) => (
                    <Select.Option
                      key={ledger.referenceId}
                      value={ledger.referenceId}
                      disabled
                    >
                      {ledger.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* <Form.Item
            label="Date:"
            layout="vertical"
            style={{
              width: 100,
            }}
          >
            <DatePicker value={selectedData.entryDate} />
          </Form.Item> */}
            <Col xs={24} md={4} lg={3}>
              <RadioButtonGroup
                name="date_type"
                initialValue={dateType}
                onChange={handleRadio}
              />
            </Col>
            <Col
              xs={dateType === 'bs' ? 0 : 24}
              md={dateType === 'bs' ? 0 : 8}
              lg={dateType === 'bs' ? 0 : 7}
            >
              <Form.Item
                name="entryDate"
                label="Entry Date(AD):"
                className="username"
                rules={[{ required: true, message: '' }]}
                hidden={dateType === 'bs'}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  className="date-picker "
                  format="YYYY-MM-DD"
                  onChange={(date) => {
                    if (date) {
                      form.setFieldsValue({
                        entry_date_bs: getBsFromAD(date),
                      });
                    }
                  }}
                  disabledDate={(current) => dateDisabled(current, 'after')}
                />
              </Form.Item>
            </Col>
            <Col
              xs={dateType === 'ad' ? 0 : 24}
              md={dateType === 'ad' ? 0 : 8}
              lg={dateType === 'ad' ? 0 : 7}
            >
              <NewNepaliDatePicker
                formProps={{
                  name: 'entry_date_bs',
                  label: 'Entry Date (BS)',
                  initialValue: getTodayDate(),
                  hidden: dateType === 'ad',
                  rules: [
                    {
                      required: true,
                      message: '',
                    },
                  ],
                }}
                disabledAfter={getTodayDate()}
                // ref={nepaliDateRef}
                onChange={(e) => {
                  if (!e) return;
                  if (e > getTodayDate()) {
                    form.setFieldsValue({ entry_date_bs: getTodayDate() });
                    return;
                  }
                  const adDate = getAdFromBS(e);
                  form.setFieldsValue({ entryDate: adDate });
                }}
              />
            </Col>
          </Row>

          <div style={{ marginTop: '20px' }}>
            <VoucherTable
              columns={columns}
              dataSource={Array.isArray(VoucherData) ? VoucherData : []}
              summary={tableSummary}
            />
          </div>
          <Form.Item label="Long Narration">
            <Input.TextArea
              value={receiptVoucherHeader?.remarks || ''}
              onChange={(e) => {
                setReceiptVoucherHeader({
                  ...receiptVoucherHeader,
                  remarks: e?.target?.value,
                });
              }}
              rows={1}
              style={{ width: 400 }}
            />
          </Form.Item>
        </Form>
        <GlobalFormModal isVisible={isModalVisible} onClose={closeModal}>
          <LedgerForm
            onClose={closeModal}
            refreshTable={refreshTable}
            showCashBankAccount={true}
          />
        </GlobalFormModal>
      </Modal>
    </>
  );
};

export default SalesReceiptModal;
